// Custom error class for API errors
class ApiError extends Error {
    status;
    errors;
    problemDetail;
    constructor(problemDetail) {
        super(problemDetail.detail || problemDetail.title || 'Request failed');
        this.name = 'ApiError';
        this.status = problemDetail.status || 500;
        this.errors = problemDetail.errors;
        this.problemDetail = problemDetail;
    }
    get isValidationError() {
        return this.status === 400 && !!this.errors;
    }
}
class HttpClient {
    baseURL;
    constructor(baseURL) {
        this.baseURL = baseURL;
    }
    getHeaders() {
        const headers = new Headers({
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        });
        const csrfToken = this.getCookie('XSRF-TOKEN');
        if (csrfToken) {
            headers.append('X-XSRF-TOKEN', csrfToken);
        }
        return headers;
    }
    getCookie(name) {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        return match ? decodeURIComponent(match[2]) : null;
    }
    async get(url) {
        const response = await fetch(`${this.baseURL}${url}`, {
            method: 'GET',
            headers: this.getHeaders(),
        });
        return this.handleResponse(response);
    }
    async post(url, data) {
        const response = await fetch(`${this.baseURL}${url}`, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify(data)
        });
        return this.handleResponse(response);
    }
    /** Multipart POST (file uploads). No Content-Type header — the browser sets it with the boundary. */
    async postMultipart(url, formData) {
        const headers = new Headers({ 'Accept': 'application/json' });
        const csrfToken = this.getCookie('XSRF-TOKEN');
        if (csrfToken) {
            headers.append('X-XSRF-TOKEN', csrfToken);
        }
        const response = await fetch(`${this.baseURL}${url}`, {
            method: 'POST',
            headers,
            body: formData
        });
        return this.handleResponse(response);
    }
    async put(url, data) {
        const response = await fetch(`${this.baseURL}${url}`, {
            method: 'PUT',
            headers: this.getHeaders(),
            body: JSON.stringify(data)
        });
        return this.handleResponse(response);
    }
    async patch(url, data) {
        const response = await fetch(`${this.baseURL}${url}`, {
            method: 'PATCH',
            headers: this.getHeaders(),
            body: JSON.stringify(data)
        });
        return this.handleResponse(response);
    }
    async delete(url) {
        const response = await fetch(`${this.baseURL}${url}`, {
            method: 'DELETE',
            headers: this.getHeaders(),
        });
        return this.handleResponse(response);
    }
    async handleResponse(response) {
        // Handle unauthorized - redirect to login
        if (response.status === 401) {
            window.location.href = '/login';
            throw new ApiError({
                status: response.status,
                title: 'Session expired',
                detail: 'Your session has expired. Please log in again.'
            });
        }
        // Try to parse response body
        let data;
        const contentType = response.headers.get('content-type');
        if (contentType && (contentType.includes('application/json') || contentType.includes('application/problem+json'))) {
            try {
                data = await response.json();
            }
            catch {
                data = null;
            }
        }
        else {
            data = null;
        }
        // Handle errors
        if (!response.ok) {
            // If we got a ProblemDetail response, use it
            if (data && (data.status || data.detail || data.title)) {
                throw new ApiError(data);
            }
            // Otherwise create a generic error
            throw new ApiError({
                status: response.status,
                title: `HTTP ${response.status}`,
                detail: `Request failed with status ${response.status}`
            });
        }
        // Success - return data directly
        return data;
    }
}
// Create and export a default instance
export default new HttpClient('');
// Export class and types
export { HttpClient, ApiError };
